import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useAppDispatch } from 'store';
import useAlert from 'infrastructure/hooks/utils/use-alert';

import { computeEngineState } from './engine/step-machine';
import { isDowngrade } from './engine/resolve-outcome';
import { buildStep1Config } from './steps/step-1-reading-status';
import { buildStep2Config } from './steps/step-2-contact-attempt';
import { step3Config } from './steps/step-3-consent';
import { buildStep4Config } from './steps/step-4-recheck';
import { buildStep6Config } from './steps/step-6-outcome';
import { buildMainBlurb } from './blurbs';
import { getChecklistsFor } from './device-checklists';
import { validateStepItems, validateSymptoms } from './validate-step';
import { readNurseMaDraft, writeNurseMaDraft } from './draft';

import type { ICriticalReadingData } from './types';
import type { IClinicalRunbook } from './use-clinical-runbook';
import type { Answers, Outcome, ReadingContext, StepId } from './engine/types';
import type { TFormItem } from '../types';

interface IProps {
  patientGuid: string;
  readingContext: ReadingContext;
  idx: string | number;
  runbook: IClinicalRunbook;
  /** reading already recovered on its own — pre-selects #1 "Already Recovered". */
  isRecovered?: boolean;
}

/** Form fields owned by each step — used to clear a step's values when it leaves the flow. */
const FIELDS_BY_STEP: Record<StepId, string[]> = {
  'reading-status': ['readingStatus', 'readingStatusReason'],
  contact: ['contact', 'unableToReachRunbook'],
  consent: [
    'consent',
    'representativeName',
    'representativeRelationship',
    'representativeAttestation',
  ],
  recheck: ['recheck', 'atRiskRunbook'],
  symptoms: ['symptomsOther', 'symptomsOtherText'],
  outcome: [
    'outcome',
    'overrideReason',
    'escalationMethod',
    'runbookAction',
    'runbookActionOther',
    'warmTransferConfirmed',
  ],
};

const toAnswers = (v: ICriticalReadingData): Answers => ({
  readingStatus: v.readingStatus,
  contact: v.contact,
  unableToReachRunbook: v.unableToReachRunbook,
  consent: v.consent,
  representativeName: v.representativeName,
  representativeRelationship: v.representativeRelationship,
  representativeAttestation: v.representativeAttestation === 'yes',
  recheck: v.recheck,
  symptoms: (() => {
    const symEntries = Object.entries(v).filter(([k]) => k.startsWith('sym_'));
    const hasOtherAnswered = v.symptomsOther !== undefined;
    if (symEntries.length === 0 && !hasOtherAnswered) return {};
    return {
      ...Object.fromEntries(symEntries.map(([k, val]) => [k, val === 'yes'])),
      other: v.symptomsOther === 'yes',
    };
  })(),
  symptomsOther: v.symptomsOtherText,
  atRiskRunbook: v.atRiskRunbook,
  runbookAction: v.runbookAction,
  runbookActionOther: v.runbookActionOther,
  outcome: v.outcome,
  overrideReason: v.overrideReason,
  escalationMethod: v.escalationMethod,
  warmTransferConfirmed: v.warmTransferConfirmed,
});

interface StepConfigCtx {
  isRecovered: boolean;
  contact: ICriticalReadingData['contact'];
  recheck: ICriticalReadingData['recheck'];
  showEscalationMethod: boolean;
  isWarmTransfer: boolean;
  showErPrecautions: boolean;
  showRunbookAction: boolean;
  showOverrideReason: boolean;
  anySymptomYes: boolean;
}

export const stepConfigMap = (
  step: StepId,
  ctx: StepConfigCtx,
  runbook: IClinicalRunbook,
): TFormItem[] => {
  switch (step) {
    case 'reading-status':
      return buildStep1Config(ctx.isRecovered);
    case 'contact':
      return buildStep2Config(runbook, ctx.contact);
    case 'consent':
      return step3Config;
    case 'recheck':
      return buildStep4Config(runbook, ctx.recheck);
    case 'outcome':
      return buildStep6Config(ctx);
    // 'symptoms' is rendered by the dedicated SymptomsStep component, not via config.
    default:
      return [];
  }
};

export const useCriticalReadingLogic = ({
  readingContext,
  idx,
  runbook,
  isRecovered,
}: IProps) => {
  const { Alert, showAlert } = useAlert();
  const {
    activityActions: { updateAction },
  } = useAppDispatch();

  const formik = useFormik<ICriticalReadingData>({
    initialValues: readNurseMaDraft(idx),
    onSubmit: () => {},
    validateOnChange: false,
  });
  const { setFieldValue } = formik;

  // If a saved draft already has a readingStatus, the pre-fills below shouldn't overwrite
  // it — the `?? existing` guards and applied-once refs handle that.

  // #1 pre-selection: recovered reading → pre-select "Already Recovered"; otherwise
  // pre-select "Still critical / at-risk". Applied once; the MA still records it as a
  // deliberate action and can change it.
  // NOTE: use setFieldValue (not setValues) — several mount effects pre-fill different
  // fields, and Formik's setValues replaces the whole object from stale closure state,
  // so parallel setValues calls clobber each other.
  const appliedRecoveredRef = useRef(false);
  useEffect(() => {
    if (appliedRecoveredRef.current) return;
    appliedRecoveredRef.current = true;
    // Respect a restored draft — only set the default when nothing is recorded yet.
    if (formik.values.readingStatus === undefined) {
      setFieldValue(
        'readingStatus',
        isRecovered ? 'already-recovered' : 'still-critical',
      );
    }
  }, [isRecovered, setFieldValue]);

  const answers = useMemo(() => toAnswers(formik.values), [formik.values]);
  const engineState = useMemo(
    () => computeEngineState(answers, readingContext),
    [answers, readingContext],
  );

  // Prune stale answers from steps that are no longer part of the flow. When the MA
  // goes Back and changes an earlier answer, later steps can drop out of visibleSteps;
  // their previously-entered values must be cleared so the blurb, validation, and
  // outcome reflect only the current path (not the abandoned one). Symptom fields are
  // dynamic (sym_*) and cleared as a group with the symptoms step.
  const { visibleSteps } = engineState;
  useEffect(() => {
    const visible = new Set<StepId>(visibleSteps);
    const stale: string[] = [];
    (Object.keys(FIELDS_BY_STEP) as StepId[]).forEach((step) => {
      if (!visible.has(step)) stale.push(...FIELDS_BY_STEP[step]);
    });
    if (!visible.has('symptoms')) {
      stale.push(
        ...Object.keys(formik.values).filter((k) => k.startsWith('sym_')),
      );
    }
    // per-key clears (setFieldValue) so this composes with concurrent mount pre-fills
    // instead of replacing the whole values object.
    stale.forEach((k) => {
      if (formik.values[k] !== undefined) setFieldValue(k, undefined);
    });
  }, [visibleSteps, setFieldValue]);

  // Apply the pre-selected outcome as the #6 default. When the MA goes back and
  // changes an earlier answer, `preSelected` recomputes; if it changes to a NEW
  // value we snap `outcome` to it and clear any stale override fields (the
  // clinical basis changed). A recompute to the SAME value leaves a deliberate
  // override intact. `appliedPreSelectRef` tracks the last value we wrote so we
  // only react to genuine changes, not to the MA's own outcome edits.
  const preSelected = engineState.preSelectedOutcome;
  const appliedPreSelectRef = useRef<Outcome | null>(null);
  useEffect(() => {
    if (!preSelected) return;
    // Re-seed also when `outcome` is currently empty: the outcome step can leave
    // the flow (e.g. the MA switches #1 to Still Critical), which prunes `outcome`
    // to undefined. When the same pre-selection later comes back around, the ref
    // still matches it, so without this check #6 would never be re-filled.
    const outcomeMissing = formik.values.outcome === undefined;
    if (appliedPreSelectRef.current === preSelected && !outcomeMissing) return;
    appliedPreSelectRef.current = preSelected;
    // per-key so this composes with the other mount pre-fills; clear stale #6 detail
    // fields since the clinical basis changed.
    setFieldValue('outcome', preSelected);
    setFieldValue('overrideReason', undefined);
    setFieldValue('escalationMethod', undefined);
    setFieldValue('runbookAction', undefined);
    setFieldValue('runbookActionOther', undefined);
    setFieldValue('warmTransferConfirmed', undefined);
  }, [preSelected, formik.values.outcome, setFieldValue]);

  const selectedOutcome = formik.values.outcome as Outcome | undefined;
  const showEscalationMethod = selectedOutcome === 'escalate-clinic';
  const isWarmTransfer = selectedOutcome === 'warm-transfer';
  const showRunbookAction = selectedOutcome === 'follow-runbook';
  const showErPrecautions =
    !!selectedOutcome &&
    selectedOutcome !== 'warm-transfer' &&
    !engineState.noLiveCall;
  // Override-reason field only shows once the MA changes the pre-selected outcome.
  const showOverrideReason =
    !!preSelected && !!selectedOutcome && selectedOutcome !== preSelected;

  const { contact, recheck } = formik.values;
  const anySymptomYes = Object.values(answers.symptoms ?? {}).some(Boolean);
  const stepCtx = useMemo(
    () => ({
      isRecovered: !!isRecovered,
      contact,
      recheck,
      showEscalationMethod,
      isWarmTransfer,
      showErPrecautions,
      showRunbookAction,
      showOverrideReason,
      anySymptomYes,
    }),
    [
      isRecovered,
      contact,
      recheck,
      showEscalationMethod,
      isWarmTransfer,
      showErPrecautions,
      showRunbookAction,
      showOverrideReason,
      anySymptomYes,
    ],
  );
  const buildItemsForStep = useCallback(
    (step: StepId): TFormItem[] => stepConfigMap(step, stepCtx, runbook),
    [stepCtx, runbook],
  );

  // Field-level errors: the set of field `name`s that are required-but-empty on the
  // step just validated. Drives the red border (per field) and scroll-to-error.
  const [errors, setErrors] = useState<string[]>([]);

  // Symptom field names shown on #5 (across all displayed checklists) — used both to
  // validate #5 and to know which sym_* radios must be answered.
  const symptomFieldNames = useMemo(
    () =>
      getChecklistsFor(readingContext.outOfRangeParams).flatMap((cl) =>
        cl.items.map((i) => i.name),
      ),
    [readingContext.outOfRangeParams],
  );

  /** Validate a step's visible required fields; store + return the empty ones. */
  const validateStep = useCallback(
    (step: StepId): string[] => {
      const found =
        step === 'symptoms'
          ? validateSymptoms(symptomFieldNames, formik.values)
          : validateStepItems(
              stepConfigMap(step, stepCtx, runbook),
              formik.values,
            );
      setErrors(found);
      return found;
    },
    [stepCtx, runbook, symptomFieldNames, formik.values],
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  // Pre-fill the runbook yes/no answers from the real clinic runbook once it
  // loads: a populated critical instruction ⇒ 'has-entry', otherwise 'no-entry'
  // (system default = Escalate to Clinic). Applied once; the MA can still change
  // the recorded answer. `appliedRunbookRef` guards against re-applying on every
  // render and against clobbering an MA edit.
  const appliedRunbookRef = useRef(false);
  useEffect(() => {
    if (runbook.loading || appliedRunbookRef.current) return;
    appliedRunbookRef.current = true;
    const entry = runbook.hasCriticalEntry ? 'has-entry' : 'no-entry';
    // per-key setFieldValue so this doesn't clobber other mount pre-fills.
    if (formik.values.atRiskRunbook === undefined) {
      setFieldValue('atRiskRunbook', entry);
    }
    // #2 Unable-to-Reach only distinguishes do-not-escalate vs no-entry; a populated
    // runbook does not by itself mean "do not escalate", so we only pre-fill the
    // no-entry default and leave do-not-escalate to the MA.
    if (
      !runbook.hasCriticalEntry &&
      formik.values.unableToReachRunbook === undefined
    ) {
      setFieldValue('unableToReachRunbook', 'no-entry');
    }
  }, [runbook.loading, runbook.hasCriticalEntry, setFieldValue]);

  const handleSave = async (): Promise<boolean> => {
    // Required-field check on the outcome step first (red border + scroll handled by caller).
    if (validateStep('outcome').length > 0) {
      return false;
    }
    // For "Follow Runbook Action", the effective outcome for the downgrade check is the
    // runbook action the MA entered (Warm Transfer / Escalate to Clinic / 911). "Other"
    // is an unknown escalation level, so it falls back to follow-runbook's own rank.
    const effectiveOutcome: Outcome | undefined =
      selectedOutcome === 'follow-runbook' &&
      formik.values.runbookAction &&
      formik.values.runbookAction !== 'other'
        ? formik.values.runbookAction
        : selectedOutcome;
    // Block a genuine downgrade (effective outcome ranks below the pre-selected one).
    if (
      preSelected &&
      effectiveOutcome &&
      effectiveOutcome !== preSelected &&
      isDowngrade(preSelected, effectiveOutcome)
    ) {
      await showAlert({
        title: 'Not allowed',
        messageTitle:
          'You cannot select a less-escalated outcome than the system pre-selected.',
        successBtnLabel: 'OK',
        showReject: false,
      });
      return false;
    }
    // Any change of the menu selection from the pre-selected outcome needs a reason.
    if (
      preSelected &&
      selectedOutcome &&
      selectedOutcome !== preSelected &&
      !formik.values.overrideReason?.trim()
    ) {
      await showAlert({
        title: 'Reason required',
        messageTitle:
          'Please enter a reason for changing the recommended outcome.',
        successBtnLabel: 'OK',
        showReject: false,
      });
      return false;
    }
    if (isWarmTransfer && !formik.values.warmTransferConfirmed) {
      await showAlert({
        title: 'Confirmation required',
        messageTitle: 'Confirm the warm transfer before saving.',
        successBtnLabel: 'OK',
        showReject: false,
      });
      return false;
    }
    const blurb = buildMainBlurb(formik.values, {
      noLiveCall: engineState.noLiveCall,
    });
    updateAction({ idx, action: { message: blurb } });
    writeNurseMaDraft(idx, formik.values);
    return true;
  };

  return {
    formik,
    engineState,
    buildItemsForStep,
    handleSave,
    errors,
    validateStep,
    clearErrors,
    symptomFieldNames,
    Alert,
  };
};
